// Phase 38 · 23b — the activity trail.
//
// Six tinted icon bubbles down a column is decoration: the reader already has
// the sentence, and the glyph only repeats it in a colour they must first learn.
// A single dot carries the one thing the glyph did usefully — whether this line
// is routine, a warning, or a breach — and the row gets its width back for the
// ticket number and the event.

import { useEffect, useRef, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { WidgetShell, WidgetEmpty } from './WidgetShell';

/**
 * formatDistanceToNow throws RangeError: Invalid time value (not just a bad
 * render) when handed an Invalid Date — a missing/null/malformed timestamp
 * on any single activity record crashed this whole widget on every render
 * (Bug #9408). The API type marks `timestamp` as required, but that's
 * compile-time only; nothing validates the BFF response at runtime.
 */
export function formatRelativeTimestamp(timestamp: string | undefined | null): string {
  const date = new Date(timestamp ?? '');
  if (Number.isNaN(date.getTime())) return '—';
  return formatDistanceToNow(date, { addSuffix: true });
}

/** "19m ago", "3h ago", "2d ago": short enough to leave the title its room. */
export function formatShortAge(timestamp: string | undefined | null, now = Date.now()): string {
  const t = new Date(timestamp ?? '').getTime();
  if (Number.isNaN(t)) return '—';
  const mins = Math.max(0, Math.floor((now - t) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

/** Height of one two-line event row; the card shows as many as fit. */
const ROW_HEIGHT = 46;

type ActivityType =
  | 'ticket_created'
  | 'ticket_resolved'
  | 'ticket_assigned'
  | 'message_received'
  | 'sla_warning'
  | 'sla_breach';

interface Activity {
  id: string;
  type: ActivityType;
  title: string;
  description: string;
  timestamp: string;
  meta?: {
    ticketId?: string;
    agentName?: string;
    customerName?: string;
  };
}

interface RecentActivityWidgetProps {
  title?: string;
  activities?: Activity[];
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  onViewAll?: () => void;
  className?: string;
}

/** Only the consequence is encoded in colour; everything else reads as routine. */
const TONE: Record<ActivityType, string> = {
  ticket_created: 'var(--co-ink)',
  ticket_resolved: 'var(--co-green)',
  ticket_assigned: 'var(--co-machine)',
  message_received: 'var(--co-ink)',
  sla_warning: 'var(--co-terracotta-ink)',
  sla_breach: 'var(--co-burgundy)',
};

const EMPHASIS: Partial<Record<ActivityType, string>> = {
  sla_warning: 'var(--co-terracotta-ink)',
  sla_breach: 'var(--co-burgundy)',
};

/**
 * The event's own detail, without the "#TICKET — " prefix the API puts on it:
 * the ticket number already has its own column, and repeating it left the
 * detail column reading "#G…" on a laptop.
 */
export function activityDetail(description: string | undefined, ticketId?: string): string {
  if (!description) return '';
  const stripped = ticketId
    ? description.replace(new RegExp(`^#?${ticketId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*[—–-]\\s*`), '')
    : description;
  return stripped.trim();
}

export function RecentActivityWidget({
  activities = [],
  loading = false,
  error,
  onRetry,
  onViewAll,
  className = '',
  title,
}: RecentActivityWidgetProps) {
  const navigate = useNavigate();

  // Show only the events that fit the card. Ten fixed rows overflowed a
  // laptop-height card and the last one was cut in half at the bottom edge.
  const listRef = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState<number | null>(null);
  useEffect(() => {
    const body = listRef.current?.parentElement;
    if (!body) return;
    const measure = () => {
      const h = body.clientHeight;
      setFit(h > 0 ? Math.max(1, Math.floor(h / ROW_HEIGHT)) : null);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(body);
    return () => ro.disconnect();
  }, [activities.length, loading]);
  const shown = fit === null ? activities : activities.slice(0, fit);

  // The BFF sets each activity's `id` to the underlying ticket's UUID (the
  // route expects the UUID; meta.ticketId is the display ticket number).
  const openTicket = (activity: Activity) => {
    if (activity.id) navigate(`/tickets/${activity.id}`);
  };

  return (
    <WidgetShell
      title={title || 'Recent Activity'}
      subtitle={loading ? undefined : `${shown.length} most recent events`}
      loading={loading}
      error={error}
      onRetry={onRetry}
      className={className}
      bodyClassName="overflow-hidden"
      action={
        onViewAll ? (
          <button
            type="button"
            onClick={onViewAll}
            className="flex items-center gap-1 text-[12.5px] text-[var(--co-ink-2)] hover:text-[var(--co-brass-ink)]"
          >
            View all
            <ArrowRight className="h-[13px] w-[13px]" />
          </button>
        ) : undefined
      }
      finding={
        activities.length > 0 ? (
          <>
            Every entry links to its ticket. The full trail, including automations and system
            actions, is in <strong className="font-semibold text-[var(--co-ink)]">Backtrack</strong>.
          </>
        ) : null
      }
      empty={
        activities.length === 0 ? (
          <WidgetEmpty title="No recent activity" hint="Events appear here as tickets move." />
        ) : undefined
      }
    >
      <div ref={listRef}>
      {shown.map((activity, i) => {
        const tone = TONE[activity.type] || 'var(--co-ink)';
        const detail = activityDetail(activity.description, activity.meta?.ticketId);
        return (
          <div
            key={activity.id}
            role="button"
            tabIndex={0}
            onClick={() => openTicket(activity)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openTicket(activity);
              }
            }}
            title={activity.title}
            style={{ height: ROW_HEIGHT }}
            className={`flex cursor-pointer flex-col justify-center gap-[3px] px-[18px] hover:bg-[var(--co-selected)] ${
              i < shown.length - 1 ? 'border-b border-[var(--co-line-subtle)]' : ''
            }`}
          >
            {/* Two short lines instead of one crowded one: the title gets the
                full width; ticket, status and age sit underneath it. */}
            <div className="flex min-w-0 items-center gap-[9px]">
              <span
                className="h-[6px] w-[6px] flex-none rounded-full"
                style={{ background: tone }}
                aria-hidden
              />
              <span className="min-w-0 flex-1 truncate text-[12.5px] font-semibold text-[var(--co-ink)]">
                {activity.title}
              </span>
              <span className="co-mono flex-none whitespace-nowrap text-[10.5px] text-[var(--co-ink-3)]">
                {formatShortAge(activity.timestamp)}
              </span>
            </div>
            <div className="co-mono flex min-w-0 items-center gap-[6px] pl-[15px] text-[10.5px] text-[var(--co-ink-3)]">
              {activity.meta?.ticketId && <span className="flex-none">{activity.meta.ticketId}</span>}
              {activity.meta?.ticketId && detail && <span aria-hidden>·</span>}
              {detail && (
                <span
                  className="min-w-0 truncate"
                  style={{ color: EMPHASIS[activity.type] || 'var(--co-ink-3)' }}
                >
                  {detail}
                </span>
              )}
            </div>
          </div>
        );
      })}
      </div>
    </WidgetShell>
  );
}
